import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { Invoice, NoteType } from '@shared-models';
import { round2, roundingTolerance } from '@shared-utils';

import { INotas } from 'src/app/@interfaces/notas.interface';

import { NoteArticleType } from '../enums/note-article-type.enum';
import { NoteItemForm } from '../models/note-item-form.model';

@Injectable({ providedIn: 'any' })
export class NoteBuilderService {
  buildNoteValues(
    controls: FormGroup<NoteItemForm>[],
    sales: Invoice['venta'],
    noteType: NoteType | undefined,
    articleType: NoteArticleType = NoteArticleType.BY_ARTICLE,
  ): INotas['valores'][0] {
    const noteValues: INotas['valores'][0] = {
      id_articulo: [],
      id_warehouse: [],
      codigo: [],
      cantidad: [],
      precio: [],
      subtotal: [],
      descuento: [],
    };

    const multiplier = noteType === 'credit' ? -1 : 1;

    controls.forEach((group, index) => {
      const qty = Number(group.controls.quantity.value) || 0;
      if (!qty) return;

      const article = group.controls.article.value;
      const price = Number(group.controls.price.value) || 0;
      const subtotal = Number(group.controls.subtotal.value) || 0;
      const warehouseId =
        sales[index]?.almacen?.[0]?.id_almacen.toString() || '0';

      noteValues.id_articulo.push(parseInt(article?.id_articulo || '0'));
      noteValues.id_warehouse.push(parseInt(warehouseId));
      noteValues.codigo.push(article?.codigo || '');
      noteValues.cantidad.push(qty * multiplier);
      // Round monetary values to 2 decimals at the source. DIAN recomputes the
      // note total from `precio` × `cantidad` (it ignores our `subtotal`), so
      // rounding the unit price here keeps the DIAN-side total clean and stops
      // full-precision floats from drifting the document above the invoice.
      noteValues.subtotal.push(round2(subtotal) * multiplier);
      noteValues.precio.push(round2(price));
      // `precio` is the undiscounted unit price, so the line discount travels as
      // the percentage the invoice line carried — Api-Dian rebuilds the
      // AllowanceCharge and the taxable base from it. BY_TOTAL prices off
      // `venta.total`, already net of the discount, so it sends none.
      noteValues.descuento.push(
        articleType === NoteArticleType.BY_TOTAL
          ? 0
          : Number(sales[index]?.descuento) || 0,
      );
    });

    return noteValues;
  }

  buildNoteObject(
    noteValues: INotas['valores'][0],
    invoiceId: string | undefined,
    noteType: NoteType | undefined,
    concept: string | null,
    totalWithTax: number,
    maxTotal?: number,
    lineCount = 1,
  ): INotas {
    const isCredit = noteType === 'credit';

    let total = round2(totalWithTax);
    // Clamp a near-full credit note to the exact remaining invoice room. When
    // the recomputed total overshoots by less than the per-line rounding budget
    // it's reconciliation noise, not a real excess, so pin it to the remaining
    // to keep the stored total and the running accredited sum within the invoice.
    if (isCredit && maxTotal !== undefined) {
      const max = round2(maxTotal);
      if (total > max && total <= max + roundingTolerance(lineCount)) {
        total = max;
      }
    }

    return {
      note_fact: parseInt(invoiceId?.toString() || '0'),
      tipo_nota: isCredit ? 'Credito' : 'Debito',
      concepto: concept || '',
      total: total * (isCredit ? -1 : 1),
      createAt: new Date().toISOString(),
      dueDate: new Date().toISOString(),
      valores: [noteValues],
    };
  }
}
